<script lang="ts">
export default {
  name: 'AccountBenchmarkingTableRow',
};
</script>

<script setup lang="ts">
import { inject, computed } from 'vue';
import { accountService } from '@/services/api';
import { ExposureMetric, ExposureValue, ExposureValueEntry } from '@/types/AccountExposure';
import { DateTime } from 'luxon';
import AccountExposureEntryModal from '../AccountExposureEntryModal.vue';
import { BenchmarkContext, Column } from './Benchmarking';
import BenchmarkingTableCell from './BenchmarkingTableCell.vue';
import { filters } from '@/utilities/Filters'; // this is used in the template but ignored by the compiler
import { Benchmark } from '@/types/Benchmark';
import { Package, Pencil, ExternalLink } from 'lucide-vue-next';

const props = defineProps<{
  data: Benchmark;
  columns: Column[];
  highlight?: boolean;
  shaded?: boolean;
  static?: boolean;
  frozen?: boolean; // rows must be contiguous and at the top of the table
}>();

const emit = defineEmits<{
  'needs-update': [];
}>();

const context = inject<BenchmarkContext>('context');

const now = new Date().toISOString();

// ---------
// computed
// ---------
const hasIEXExposure = computed(() => {
  const now = DateTime.local().startOf('day').toISODate();
  const data = context?.injectedExposureData?.filter(x => x.uploadedBy === 'IEX Cloud' && DateTime.fromISO(x.uploadTimeStamp).startOf('day').toISODate() === now);
  return data && data.length > 0;
});

// ----------
// functions
// ----------
function getCategoryValueFromName(categoryName: string): number {
  return context?.injectedExposureCategories ? context?.injectedExposureCategories[categoryName] : 0;
}

function selectedExposureTypes(category: string | number, exposureMetricId? : number): ExposureMetric[] {
  let categoryValue: number;
  if (typeof category === 'string') {
    categoryValue = getCategoryValueFromName(category);
  } else {
    categoryValue = category;
  }

  return context?.injectedExposureTypes?.filter(x => {
    return (x.exposureCategoryID === categoryValue) && (exposureMetricId ? x.exposureMetricID === exposureMetricId : true);
  }) ?? [];
}

function selectedExposureData(categoryName: string): ExposureValue[] {
  const category = getCategoryValueFromName(categoryName);
  return context?.injectedExposureData?.filter(x => x.categoryID === category) ?? [];
}

async function insertExposureValues(entries: ExposureValueEntry[]): Promise<void> {
  await accountService.submitExposures(props.data.accountId as string || '', entries);
  emit('needs-update');
}

function canEnter(categoryName: string): boolean {
  const category = getCategoryValueFromName(categoryName);
  if (!hasIEXExposure.value) {
    return true;
  }
  return selectedExposureTypes(category).length > 0;
}

function cellClick(column: Column, data: unknown): void {
  if (props.static) {
    return;
  }
  if (!column.url) {
    return;
  }
  const url = column.url(data);
  if (url) {
    window.open(url, '_blank')?.focus();
  }
}

function getUrl(column: Column, data: unknown): string {
  if (props.static) {
    return '';
  }
  if (!column.url) {
    return '';
  }
  return column.url(data);
}
</script>

<template>
  <tr :class="{ highlight, shaded, frozen }" class="benchmarking-row">

    <template v-for="(column, index) in columns" :key="index">
      <td v-if="!column.useFilteredExport" :key="index"
          :class="`${column.type} ${column.classes || ''} ${column.url && !static ? 'clickable-benchmarking-cell' : ''}`"
          :data-raw="data[column.fieldName]"
          @click.prevent="cellClick(column, data)"
      >
        <BenchmarkingTableCell :url="getUrl(column, data)" :hasUrl="!!column.url">
          <!-- @vue-skip-->
          <template v-if="column.filter">
            {{ filters[column.filter](data[column.fieldName]) ?? '--' }}
          </template>
          <template v-else>
            {{ data[column.fieldName] ?? '--' }}
          </template>
          <template v-slot:accessoryIcon>
            <div class="tw:inline">
              <span
                class="url-icon tw:pl-2"
                :class="{ 'static-icon': static }"
                v-if="column.url"
                :data-test="`url-${data[column.fieldName]}-${index}`">
                <component
                  :is="column.accessoryIcon === 'pencil' ? Pencil : ExternalLink"
                  :size="14"
                  class="tw:inline tw:text-primary" />
              </span>

              <div v-if="column.showInfoIcon && column.showInfoFieldName && data[column.showInfoIcon]"
                   class="tw:inline tw:pl-2">
                <div class="tw:tooltip tw:tooltip-top tw:top-0.5 tw:z-50 tw:before:max-w-fit" :data-tip="data[column.showInfoFieldName]">
                  <Package class="tw:text-gray-500" :size="16" />
                </div>
              </div>
            </div>
          </template>

          <AccountExposureEntryModal
              v-if="column.exposureCategory"
              :exposureTypes="selectedExposureTypes(column.exposureCategory, column.exposureMetricId)"
              :canEnter="canEnter(column.exposureCategory)"
              :insert="insertExposureValues"
              invalidEntryReason=""
              :exposureData="selectedExposureData(column.exposureCategory)"
              :initialEffectiveDate="data.policyEffectiveDate || now"
          >
            <template v-slot:button="{ props }">
              <span class="url-icon tw:pl-2" :class="{'static-icon': static}" v-bind="props">
                <Pencil :size="14" class="tw:inline tw:text-primary" />
              </span>
            </template>
          </AccountExposureEntryModal>

        </BenchmarkingTableCell>
      </td>
      <td
          v-else
          :class="`${column.type} ${column.classes || ''} ${column.url && !static ? 'clickable-benchmarking-cell' : ''}`"
      >
        <!-- @vue-skip-->
        <template v-if="column.filter">
          {{ filters[column.filter](data[column.fieldName]) ?? '--' }}
        </template>
        <template v-else>
          {{ data[column.fieldName] ?? '--' }}
        </template>
        <span class="url-icon" v-if="column.url && !static && data[column.fieldName]">
          <ExternalLink :size="14" class="tw:inline tw:text-primary" />
        </span>
      </td>
    </template>
  </tr>
</template>

<style>
.truncated a,
.truncate a {
  display: inline-block;
  width: 15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.url-icon {
  visibility: hidden;
}

@media (pointer: none), (pointer: coarse) {
  .url-icon {
    visibility: visible;
  }
}

.benchmarking-row:hover .url-icon {
  visibility: visible;
}

.static-icon {
  visibility: hidden !important;
}

tr > td.compactmoney {
  min-width: 8em;
}
</style>
